import React, { useEffect, useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';

const overlayBaseStyle = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 20000, // Must be above sidebar (Sidebar.css uses 10001)
};

const modalStyle = {
  backgroundColor: '#0f0f0f',
  color: '#e0e0e0',
  padding: '18px',
  borderRadius: '10px',
  border: '1px solid #2a2a2a',
  width: '92vw',
  maxWidth: '560px',
  maxHeight: '80vh',
  overflowY: 'auto',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6)',
};

const headerRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  marginBottom: '12px',
};

const actionBtnStyle = {
  background: 'transparent',
  border: '1px solid #2a2a2a',
  color: '#e0e0e0',
  borderRadius: '8px',
  padding: '8px 10px',
  cursor: 'pointer',
  fontWeight: 600,
};

const iconBtnStyle = {
  ...actionBtnStyle,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const primaryBtnStyle = {
  ...actionBtnStyle,
  background: 'var(--brand-accent-gradient)',
  border: 'none',
  color: '#fff',
};

export default function RunGroupPickerModal({
  isOpen,
  title,
  runName,
  groups,
  selectedGroupIds,
  onToggleGroup,
  onCreateGroup,
  onClose,
  busy,
}) {
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const [newGroupName, setNewGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const measure = () => {
      const el = document.querySelector('.sidebar');
      const w = el && el.getBoundingClientRect ? el.getBoundingClientRect().width : 0;
      setSidebarWidth(Number.isFinite(w) ? w : 0);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isOpen]);

  const sortedGroups = useMemo(() => {
    const list = Array.isArray(groups) ? [...groups] : [];
    list.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
    return list;
  }, [groups]);

  if (!isOpen) return null;

  const overlayStyle = {
    ...overlayBaseStyle,
    left: sidebarWidth ? `${sidebarWidth}px` : 0,
    width: sidebarWidth ? `calc(100% - ${sidebarWidth}px)` : '100%',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerRowStyle}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 800 }}>
              {title || 'Groups'}
            </div>
            {runName ? (
              <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                {runName}
              </div>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              style={iconBtnStyle}
              onClick={onClose}
              aria-label="Close"
              title="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div style={{ borderTop: '1px solid #2a2a2a', paddingTop: '12px' }}>
          {sortedGroups.length === 0 ? (
            <div style={{ color: '#888', fontSize: '14px' }}>
              No groups yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {sortedGroups.map((g) => {
                const checked = (selectedGroupIds || []).includes(g.id);
                return (
                  <label
                    key={g.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '10px 12px',
                      background: '#151515',
                      border: '1px solid #2a2a2a',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      opacity: busy ? 0.7 : 1,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!!busy || creating}
                        onChange={() => onToggleGroup?.(g.id, !checked)}
                      />
                      <div style={{ fontWeight: 700 }}>{g.name}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {onCreateGroup && (
          <div style={{ marginTop: '14px', borderTop: '1px solid #2a2a2a', paddingTop: '12px' }}>
            <div style={{ fontSize: '13px', color: '#888', marginBottom: '8px' }}>
              Can't find it? Create a new group:
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="New group name"
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #2a2a2a',
                  background: '#111',
                  color: '#e0e0e0',
                  outline: 'none',
                }}
                disabled={!!busy || creating}
              />
              <button
                type="button"
                style={primaryBtnStyle}
                disabled={!!busy || creating || !newGroupName.trim()}
                onClick={async () => {
                  const name = newGroupName.trim();
                  if (!name) return;
                  try {
                    setCreating(true);
                    const createdId = await onCreateGroup({ name, notes: '' });
                    if (createdId && createdId !== false) {
                      setNewGroupName('');
                      // Auto-attach the run to the newly created group
                      onToggleGroup?.(String(createdId), true);
                    }
                  } finally {
                    setCreating(false);
                  }
                }}
              >
                + Create
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

